import React from 'react';

import { useIntl } from 'react-intl-next';

import { PopupSelect } from '@atlaskit/select';
import {
	getOptionKey,
	RESULTS_SORT_FIELDS,
	type ResultsSortOptionKey,
	ResultsSortOptions,
	SORT_FIELD_KEY,
} from '@atlassian/search-client';

import { ProductKeys } from '../../../../../common/constants/products';
import { FilterButton } from '../../../../../common/ui/filters/filter-button';
import { FilterButtonOld } from '../../../../../common/ui/filters/filter-button-old';
import { matchReferenceWidthModifier } from '../../../../../common/ui/filters/utils/popper';
import {
	useFiltersDiscoverability2Enabled,
	useResultsSort,
	useSelectedProduct,
} from '../../../../../controllers/store';

import { messages } from './messages';

type ResultsSortProps = {
	isFilterInDropdown?: boolean;
	matchReferenceWidth?: boolean;
};

export const ResultsSort = ({
	isFilterInDropdown,
	matchReferenceWidth = isFilterInDropdown,
}: ResultsSortProps) => {
	const { formatMessage } = useIntl();
	const [selectedProduct] = useSelectedProduct();
	const [{ order, field }, { setSortField, setSortOrder }] = useResultsSort();
	const filtersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const handleSelectOption = (option: { label: string; value: ResultsSortOptionKey }) => {
		setSortField(RESULTS_SORT_FIELDS[option.value].field);
		setSortOrder(RESULTS_SORT_FIELDS[option.value].order);
	};

	if (selectedProduct !== ProductKeys.Confluence) {
		return null;
	}

	const selectedOptionKey = getOptionKey(field, order);

	const options = ResultsSortOptions.map((option) => ({
		label: formatMessage(messages[option.labelKey]),
		value: option.labelKey,
		isSelected: option.labelKey === selectedOptionKey,
	}));

	return (
		<PopupSelect
			label={formatMessage(messages.sortOptionsLabel)}
			blurInputOnSelect={false}
			tabSelectsValue={false}
			backspaceRemovesValue={false}
			hideSelectedOptions={false}
			controlShouldRenderValue={false}
			isClearable={false}
			maxMenuWidth="22rem"
			maxMenuHeight={400}
			menuPlacement="bottom"
			isOptionSelected={(option) => option.isSelected}
			onChange={(value) => value && handleSelectOption(value)}
			options={options}
			popperProps={{
				strategy: 'fixed',
				modifiers: [
					{ name: 'offset', options: { offset: [0, 8] } },
					{
						name: 'preventOverflow',
						enabled: false,
					},
					...(matchReferenceWidth ? [matchReferenceWidthModifier] : []),
				],
			}}
			target={({ isOpen, ...triggerProps }) => {
				const isSelected = isOpen || field !== SORT_FIELD_KEY.RELEVANCE;
				return filtersDiscoverability2Enabled ? (
					<FilterButton
						{...triggerProps}
						isSelected={isSelected}
						testId="results-sort-button"
						filterOrControlId="sort"
						isFilterInDropdown={isFilterInDropdown}
					>
						{formatMessage(messages[RESULTS_SORT_FIELDS[selectedOptionKey].selectedLabelKey])}
					</FilterButton>
				) : (
					<FilterButtonOld
						isSelected={isSelected}
						otherProps={triggerProps}
						label={formatMessage(messages[RESULTS_SORT_FIELDS[selectedOptionKey].selectedLabelKey])}
						filterOrControlId={'sort'}
					/>
				);
			}}
		/>
	);
};
